// src/pages/AuthGatewayPage.tsx
import React, { useState, useEffect } from 'react';
import { auth, db } from '../config/firebase'; 
import { 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    updateProfile, 
    sendEmailVerification, 
    sendPasswordResetEmail,
    deleteUser
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, collection, addDoc, query, where, getDocs, limit } from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { buildMandantNr, isValidMonthYearCore } from '../utils/mandantNrFormat'; 
import {
    normalizeCustomerNumberCore,
} from '../utils/bestandMatch';
import useAppStore from '../store/appStore'; 
import { resolveTeamMemberByEmail } from '../utils/teamAuth';
import { migrateTeamRegistry } from '../store/slices/coreDataSlice';
import { 
    LogIn, UserPlus, Mail, Key, ArrowRight, Check, AlertCircle, MailQuestion, Loader2,
    ShieldCheck, UserCircle, ArrowLeft, FileText, X, Info, Eye, EyeOff, PlayCircle
} from 'lucide-react';

import ContractPreviewBasic from '../components/contracts/ContractPreviewBasic';
import ContractPreviewInsider from '../components/contracts/ContractPreviewInsider';
import { PE_ONE_LOGO_SRC } from '../constants/brandAssets';

type AuthView = 'landing' | 'login' | 'register' | 'reset';

/** Kurzes Anleitungsvideo (Querformat) — Registrierung / Account-Setup & Onboarding */
const REGISTER_GUIDE_VIMEO_ID = '1230131757';
const REGISTER_GUIDE_VIMEO_EMBED = `https://player.vimeo.com/video/${REGISTER_GUIDE_VIMEO_ID}?title=0&byline=0&portrait=0`;

/** Pending P1-Bootstrap nach Login mit bestehendem PEBase-/Auth-Konto */
const P1_PENDING_EXISTING_AUTH_KEY = 'P1_pending_existing_auth_onboarding';
/** Client-Rate-Limit: Zeitstempel des letzten echten Registrierungsversuchs (pro Tab) */
const P1_REGISTER_ATTEMPT_TS_KEY = 'P1_register_attempt_ts';
const P1_REGISTER_COOLDOWN_MS = 30000;

type PendingExistingAuthPayload = {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    isExistingClient: boolean;
    providedOldId: string;
    finalMandantNr: string;
    isSolo: boolean;
    hasEmployeeEval: boolean | null;
    isTeamProPlus?: boolean;
    tarifStatus: string;
    packageTier: string;
    registrationYear: string;
    vertragsbeginn: string;
    contractAccepted: boolean;
    isExistingCustomer?: boolean;
    existingCustomerNumber?: string;
    customerNumber?: string;
    billingStatus?: 'pending_approval' | 'active';
    sepaCancelPending?: boolean;
    needsManualBestandReview?: boolean;
    bestandMatched?: boolean;
};

/** iOS PWA: Zielroute vor Auth-Gateway sichern (z. B. /pepulse). */
export const PWA_RETURN_URL_KEY = 'pWA_return_url';

function normalizePathname(pathname: string): string {
    const p = String(pathname || '/').split('?')[0];
    const trimmed = p.replace(/\/+$/, '');
    return trimmed === '' ? '/' : trimmed;
}

/** Nur relative App-Routen — kein Root, kein Open-Redirect. */
export function isSaveablePwaReturnPath(pathname: string): boolean {
    const p = normalizePathname(pathname);
    if (p === '/') return false;
    if (!p.startsWith('/') || p.startsWith('//')) return false;
    if (p.includes('://')) return false;
    return true;
}

/**
 * Speichert pathname+search, sofern sinnvoll.
 * Überschreibt einen bereits gesetzten Key nicht (Deep-Link / PWA start_url).
 */
export function capturePwaReturnUrl(): void {
    try {
        if (typeof window === 'undefined') return;
        const pathname = window.location.pathname || '/';
        const search = window.location.search || '';
        if (!isSaveablePwaReturnPath(pathname)) return;
        const existing = sessionStorage.getItem(PWA_RETURN_URL_KEY);
        if (existing && isSaveablePwaReturnPath(existing.split('?')[0] || '/')) {
            return;
        }
        sessionStorage.setItem(PWA_RETURN_URL_KEY, pathname + search);
    } catch {
        /* ignore */
    }
}

/** Liest und löscht pWA_return_url; Fallback wenn ungültig/fehlend. */
export function resolvePostLoginHref(fallback: string): string {
    try {
        if (typeof window === 'undefined') return fallback;
        const raw = sessionStorage.getItem(PWA_RETURN_URL_KEY);
        if (!raw) return fallback;
        sessionStorage.removeItem(PWA_RETURN_URL_KEY);
        const pathOnly = raw.split('?')[0] || '/';
        if (!isSaveablePwaReturnPath(pathOnly)) return fallback;
        if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) {
            return fallback;
        }
        return raw;
    } catch {
        return fallback;
    }
}

const AuthGatewayPage: React.FC = () => {
    const { settings, team } = useAppStore(state => ({ 
        settings: state.settings,
        team: state.team
    }));
    const coaches = team ? team.filter(member => member.category === 'coach' && member.name) : [];

    const [view, setView] = useState<AuthView>('landing');
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error'; visible: boolean }>({ msg: '', type: 'success', visible: false });

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone] = useState('');

    const [isExistingClient, setIsExistingClient] = useState<boolean | null>(null);
    const [providedMandantNr, setProvidedMandantNr] = useState('');
    
    const [isSolo, setIsSolo] = useState<boolean | null>(null);
    const [hasEmployeeEval, setHasEmployeeEval] = useState<boolean | null>(null);
    
    const [contractAccepted, setContractAccepted] = useState(false); 
    const [isContractModalOpen, setIsContractModalOpen] = useState(false);
    /** Banner: Login nach auth/email-already-in-use (PEBase-Bestandskonto) */
    const [existingAccountNotice, setExistingAccountNotice] = useState(false);
    /** E-Mail Schreibschutz nach email-already-in-use (Schleuse 1) */
    const [emailLocked, setEmailLocked] = useState(false);
    /** 🍯 Honeypot: für Menschen unsichtbar — Bots füllen es aus */
    const [botField, setBotField] = useState('');

    // Live-Daten & Rohdaten-Monitor
    const [liveGlobalSettings, setLiveGlobalSettings] = useState<any>(null);
    const [rawFirebasePayload, setRawFirebasePayload] = useState<any>(null); 

    useEffect(() => {
        capturePwaReturnUrl();
    }, []);

    useEffect(() => {
        const fetchLiveSettings = async () => {
            try {
                const docRef = doc(db, 'system_data', 'global_settings');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const rawData = docSnap.data();
                    setRawFirebasePayload(rawData); 
                    
                    let extractedData = rawData;
                    if (rawData?.settings?.global) {
                        extractedData = rawData.settings.global;
                    } else if (rawData?.settings) {
                        extractedData = rawData.settings;
                    }
                    
                    setLiveGlobalSettings(extractedData);
                }
            } catch (err) {
                console.error("Fehler beim Laden der globalen Live-Settings:", err);
            }
        };
        fetchLiveSettings();
    }, []);

    // Exaktes Mapping
    const neukundenAufschlag = Number(liveGlobalSettings?.Neukunden_Aufschlag ?? 0);
    const teamplusPercentage = Number(liveGlobalSettings?.Grobkundenaufschlag ?? 0);
    const rawSoloPrice = Number(liveGlobalSettings?.Insider_Solo ?? 0);
    const rawTeamPrice = Number(liveGlobalSettings?.Insider_Team ?? 0);

    // Dynamische Berechnung der Schwelle
    const teamplusThreshold = teamplusPercentage > 0 
        ? Math.round(rawTeamPrice / (teamplusPercentage / 100)) 
        : Number(liveGlobalSettings?.Grobkundenaufschlag_ab ?? 0);

    // Dynamischer Aufschlag für Neukunden
    const getCalculatedPrice = (baseVal: number) => {
        if (!baseVal) return 0;
        return isExistingClient === false ? baseVal * (1 + (neukundenAufschlag / 100)) : baseVal;
    };

    const formatCurrency = (val: number) => val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatPercent = (val: number) => val.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
    
    const renderPrice = (val: number) => liveGlobalSettings ? formatCurrency(val) : '...';
    const renderPercent = (val: number) => liveGlobalSettings ? formatPercent(val) : '...';

    const soloPrice = getCalculatedPrice(rawSoloPrice);
    const teamPrice = getCalculatedPrice(rawTeamPrice);

    const mergedSettings = {
        ...settings,
        global: {
            ...settings?.global,
            ...liveGlobalSettings
        }
    };

    const showToast = (msg: string, type: 'success' | 'error') => {
        setToast({ msg, type, visible: true });
        setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 5000);
    };

    const savePendingExistingAuth = (payload: PendingExistingAuthPayload) => {
        try {
            localStorage.setItem(P1_PENDING_EXISTING_AUTH_KEY, JSON.stringify(payload));
        } catch {
            /* ignore */
        }
    };

    const loadPendingExistingAuth = (): PendingExistingAuthPayload | null => {
        try {
            const raw = localStorage.getItem(P1_PENDING_EXISTING_AUTH_KEY);
            if (!raw) return null;
            return JSON.parse(raw) as PendingExistingAuthPayload;
        } catch {
            return null;
        }
    };

    const clearPendingExistingAuth = () => {
        try {
            localStorage.removeItem(P1_PENDING_EXISTING_AUTH_KEY);
        } catch {
            /* ignore */
        }
    };

    /** Optional: Vorname/Nachname/Telefon aus pebase_users/{uid} */
    const readPebaseProfile = async (uid: string): Promise<{ firstName: string; lastName: string; phone: string }> => {
        try {
            const snap = await getDoc(doc(db, 'pebase_users', uid));
            if (!snap.exists()) return { firstName: '', lastName: '', phone: '' };
            const d = snap.data() || {};
            let fn = String(d.firstName || d.vorname || '').trim();
            let ln = String(d.lastName || d.nachname || '').trim();
            const ph = String(d.phone || d.telefon || d.whatsapp || d.whatsappNr || '').trim();
            if ((!fn || !ln) && typeof d.name === 'string' && d.name.trim()) {
                const parts = d.name.trim().split(/\s+/);
                if (!fn) fn = parts[0] || '';
                if (!ln) ln = parts.slice(1).join(' ') || '';
            }
            return { firstName: fn, lastName: ln, phone: ph };
        } catch (e) {
            console.warn('pebase_users Prefill fehlgeschlagen:', e);
            return { firstName: '', lastName: '', phone: '' };
        }
    };

    /** P1-Dokumente unter bestehender UID anlegen/ergänzen */
    const bootstrapP1Documents = async (
        user: import('firebase/auth').User,
        payload: PendingExistingAuthPayload,
        options?: { skipEmailVerification?: boolean }
    ) => {
        const peBase = await readPebaseProfile(user.uid);
        const fn = (payload.firstName || peBase.firstName || '').trim();
        const ln = (payload.lastName || peBase.lastName || '').trim();
        const ph = (payload.phone || peBase.phone || '').trim();
        const em = (payload.email || user.email || '').trim();

        if (fn || ln) {
            try {
                await updateProfile(user, { displayName: `${fn} ${ln}`.trim() });
            } catch (e) {
                console.warn('updateProfile übersprungen:', e);
            }
        }

        if (!options?.skipEmailVerification && !user.emailVerified) {
            try {
                auth.languageCode = 'de';
                await sendEmailVerification(user);
            } catch (e) {
                console.warn('sendEmailVerification übersprungen:', e);
            }
        }

        try {
            const mandantenCol = collection(db, 'kalkulation_mandanten');
            const [topHit, nestedHit] = await Promise.all([
                getDocs(query(mandantenCol, where('mandantNr', '==', payload.finalMandantNr), limit(1))),
                getDocs(query(mandantenCol, where('clientData.mandantNr', '==', payload.finalMandantNr), limit(1))),
            ]);
            const collision =
                topHit.docs.some((d) => d.id !== user.uid) ||
                nestedHit.docs.some((d) => d.id !== user.uid);
            if (collision) {
                throw new Error('Stopp, Doppelvergabe.');
            }
        } catch (dupErr: any) {
            if (String(dupErr?.message || '').includes('Doppelvergabe')) throw dupErr;
            console.warn('Doppelvergabe-Check übersprungen (Rules):', dupErr);
        }

        // Scheibe 1: Kundennummern-/Abrechnungsfelder — Fallbacks gegen undefined
        // (Firestore wirft bei undefined; Payload-Felder sind optional, z. B. aus älterem Build)
        const resolvedIsExistingCustomer = payload.isExistingCustomer ?? !!payload.isExistingClient;
        const resolvedCustomerNumber = payload.customerNumber ?? (payload.providedOldId || 'PENDING');
        const resolvedExistingCustomerNumber = payload.existingCustomerNumber ?? payload.providedOldId ?? '';
        const resolvedSepaCancelPending = payload.sepaCancelPending ?? false;

        // Überschreibschutz: ein bereits aktiver Abrechnungsstatus wird nie auf pending zurückgesetzt
        let resolvedBillingStatus: 'pending_approval' | 'active' = payload.billingStatus ?? 'pending_approval';
        try {
            const billingSnap = await getDoc(doc(db, 'kalkulation_mandanten', user.uid));
            const billingData: any = billingSnap.exists() ? billingSnap.data() : null;
            if (billingData && billingData.billingStatus === 'active') {
                resolvedBillingStatus = 'active';
            }
        } catch (billingErr) {
            console.warn('billingStatus-Check übersprungen (Rules):', billingErr);
        }

        await setDoc(doc(db, 'users', user.uid), {
            firstName: fn,
            lastName: ln,
            phone: ph,
            email: em,
            role: 'mandant',
            isUnlockedByAdmin: false,
            createdAt: serverTimestamp(),
            isBestandskunde: payload.isExistingClient,
            providedOldId: payload.providedOldId,
            mandantNr: payload.finalMandantNr,
            isSolo: payload.isSolo,
            hasEmployeeEval: payload.hasEmployeeEval,
            tarifStatus: payload.tarifStatus,
            packageTier: payload.packageTier || payload.tarifStatus,
            registrationYear: payload.registrationYear,
            vertragsbeginn: payload.vertragsbeginn,
            initialContractAccepted: payload.contractAccepted,
            isExistingCustomer: resolvedIsExistingCustomer,
            existingCustomerNumber: resolvedExistingCustomerNumber,
            customerNumber: resolvedCustomerNumber,
            billingStatus: resolvedBillingStatus,
            sepaCancelPending: resolvedSepaCancelPending,
            needsManualBestandReview: !!payload.needsManualBestandReview,
            bestandMatched: !!payload.bestandMatched,
        }, { merge: true });

        await setDoc(doc(db, 'kalkulation_mandanten', user.uid), {
            vorname: fn,
            nachname: ln,
            email: em,
            telefon: ph,
            whatsappNr: ph,
            isUnlockedByAdmin: false,
            onboardingStatus: 'started',
            mandantNr: payload.finalMandantNr,
            providedOldId: payload.providedOldId,
            isBestandskunde: payload.isExistingClient,
            isSolo: payload.isSolo,
            hasEmployeeEval: payload.hasEmployeeEval,
            isTeamProPlus: !!payload.isTeamProPlus,
            insiderTeamleaderPlusChecked: !!payload.isTeamProPlus,
            insiderTeamleaderStandardChecked: !payload.isSolo && !payload.isTeamProPlus,
            insiderSoloChecked: payload.isSolo,
            tarifStatus: payload.tarifStatus,
            packageTier: payload.packageTier || payload.tarifStatus,
            unlockedPremiumTools: [],
            vertragsbeginn: payload.vertragsbeginn,
            isExistingCustomer: resolvedIsExistingCustomer,
            existingCustomerNumber: resolvedExistingCustomerNumber,
            customerNumber: resolvedCustomerNumber,
            billingStatus: resolvedBillingStatus,
            sepaCancelPending: resolvedSepaCancelPending,
            needsManualBestandReview: !!payload.needsManualBestandReview,
            bestandMatched: !!payload.bestandMatched,
            'clientData.vorname': fn,
            'clientData.nachname': ln,
            'clientData.email': em,
            'clientData.mailadresse': em,
            'clientData.telefon': ph,
            'clientData.whatsappNr': ph,
            'clientData.mandantNr': payload.finalMandantNr,
            'clientData.unlockedPremiumTools': [],
            'clientData.vertragsbeginn': payload.vertragsbeginn,
            'clientData.tarifStatus': payload.tarifStatus,
            'clientData.packageTier': payload.packageTier || payload.tarifStatus,
            'clientData.isSolo': payload.isSolo,
            'clientData.hasEmployeeEval': payload.hasEmployeeEval,
            'clientData.isTeamProPlus': !!payload.isTeamProPlus,
            'clientData.insiderTeamleaderPlusChecked': !!payload.isTeamProPlus,
            'clientData.insiderTeamleaderStandardChecked': !payload.isSolo && !payload.isTeamProPlus,
            'clientData.insiderSoloChecked': payload.isSolo,
            'clientData.isBestandskunde': payload.isExistingClient,
            'clientData.isExistingCustomer': resolvedIsExistingCustomer,
            'clientData.customerNumber': resolvedCustomerNumber,
            'clientData.billingStatus': resolvedBillingStatus,
        }, { merge: true });

        try {
            const fullName = `${fn} ${ln}`.trim();
            await addDoc(collection(db, 'mail_queue'), {
                email: 'erfolg@friseur-coaching.de',
                type: 'registration',
                subject: `Neue Registrierung: ${fullName || em}`,
                participantName: fullName || em,
                name: fullName || em,
                vorname: fn,
                nachname: ln,
                firstName: fn,
                lastName: ln,
                firma: '',
                company: '',
                salonName: '',
                applicantEmail: em,
                applicantUid: user.uid,
                phone: ph,
                appBaseUrl: window.location.origin,
                status: 'pending',
                createdAt: serverTimestamp(),
                fromExistingAuth: true,
            });
        } catch (mailErr) {
            console.error('Registrierungs-Mail Trigger fehlgeschlagen:', mailErr);
        }
    };

    const switchToExistingAccountLogin = (hintEmail?: string) => {
        if (hintEmail) setEmail(hintEmail);
        // F1: Registrierungs-Passwort nie in die Login-Maske übernehmen
        setPassword('');
        setEmailLocked(true);
        setExistingAccountNotice(true);
        setView('login');
        showToast(
            'Ein PreisExpert-Konto existiert bereits für diese E-Mail-Adresse (z. B. aus PEBase). Bitte melde dich an, um mit dem Onboarding fortzufahren.',
            'error'
        );
    };

    /** Schleuse 2: P1-Akte prüfen — ohne aktive Akte nie Dashboard (auch nicht via PWA-Return-URL). */
    const routeAfterP1AkteCheck = async (uid: string): Promise<'onboarding' | 'dashboard'> => {
        let akteSnap;
        try {
            akteSnap = await getDoc(doc(db, 'kalkulation_mandanten', uid));
        } catch (e) {
            console.warn('Login: kalkulation_mandanten nicht lesbar — Zwang Onboarding', e);
            showToast(
                'Willkommen bei P1! Dein PreisExpert-Konto aus PEBase wurde erkannt. Bitte schließe jetzt dein P1-Onboarding ab.',
                'success'
            );
            return 'onboarding';
        }

        if (!akteSnap.exists()) {
            // Fall A: reine PEBase-/Auth-UID ohne P1-Akte
            showToast(
                'Willkommen bei P1! Dein PreisExpert-Konto aus PEBase wurde erkannt. Bitte schließe jetzt dein P1-Onboarding ab.',
                'success'
            );
            return 'onboarding';
        }

        const akte: any = akteSnap.data() || {};
        const aktiv =
            akte.isUnlockedByAdmin === true ||
            akte.clientData?.isUnlockedByAdmin === true;

        if (aktiv) {
            // Fall B: P1-Akte vorhanden & freigeschaltet
            return 'dashboard';
        }

        // Akte existiert, Onboarding/Freigabe noch offen
        showToast('Bitte schließe dein P1-Onboarding ab.', 'success');
        return 'onboarding';
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password === '90') {
            localStorage.setItem('MASTER_KEY', '90');
            window.location.reload();
            return;
        }

        if (!email || !password) return showToast("Bitte E-Mail und Passwort eingeben.", "error");

        setIsLoading(true);
        let pendingPayload = loadPendingExistingAuth();

        // 1) Nur der eigentliche Auth-Schritt darf "Fehlerhafte Zugangsdaten" melden
        let cred;
        try {
            cred = await signInWithEmailAndPassword(auth, email, password);
        } catch (err: any) {
            console.error('Login fehlgeschlagen:', err?.code || err);
            showToast("Fehlerhafte Zugangsdaten.", "error");
            setIsLoading(false);
            return;
        }

        // F1: Pending-Payload strikt an die eingeloggte Identität binden.
        // Stimmt die E-Mail nicht mit dem Payload überein (anderes Konto im selben Tab),
        // Payload sofort verwerfen und normalen Login fahren — nie fremde Daten mergen.
        if (pendingPayload) {
            const pendingMail = String(pendingPayload.email || '').trim().toLowerCase();
            const loggedMail = String(cred.user.email || '').trim().toLowerCase();
            if (!pendingMail || !loggedMail || pendingMail !== loggedMail) {
                clearPendingExistingAuth();
                pendingPayload = null;
            }
        }

        showToast("Erfolgreich eingeloggt! Lade Umgebung...", "success");

        // 2) Alles danach ist Routing — Fehler hier dürfen den Login nicht als
        //    "fehlerhaft" maskieren, sondern führen best-effort in die App
        try {
            // Team-Daten frisch aus global_settings laden (Race: Store oft noch leer)
            let teamData: any[] = team || [];
            let teamRegistryData: any[] = useAppStore.getState().teamRegistry || [];
            try {
                let sysRef = doc(db, 'system_data', 'global_settings');
                let sysDoc = await getDoc(sysRef);
                if (!sysDoc.exists()) {
                    sysRef = doc(db, 'system_data', 'master');
                    sysDoc = await getDoc(sysRef);
                }
                if (sysDoc.exists()) {
                    const sysData = sysDoc.data();
                    teamData = sysData?.team || teamData;
                    teamRegistryData = migrateTeamRegistry(
                        teamData,
                        sysData?.teamRegistry || teamRegistryData
                    );
                    useAppStore.setState({
                        team: teamData,
                        teamRegistry: teamRegistryData,
                    });
                }
            } catch (e) {
                console.error('Login: Team-Daten konnten nicht geladen werden', e);
            }

            const coachInfo = resolveTeamMemberByEmail(cred.user.email, teamData, teamRegistryData);
            if (coachInfo.isTeam) {
                clearPendingExistingAuth();
                window.location.href = resolvePostLoginHref('/');
                return;
            }

            // Bestandskonto nach Registrierungsversuch: P1-Docs unter derselben UID, dann Zwang Onboarding
            if (pendingPayload) {
                // F1: Bereits freigeschaltete Akte NIEMALS überschreiben oder ins Onboarding zwingen
                try {
                    const akteSnap = await getDoc(doc(db, 'kalkulation_mandanten', cred.user.uid));
                    const akte: any = akteSnap.exists() ? akteSnap.data() : null;
                    const alreadyUnlocked = !!akte && (
                        akte.isUnlockedByAdmin === true ||
                        akte.clientData?.isUnlockedByAdmin === true
                    );
                    if (alreadyUnlocked) {
                        clearPendingExistingAuth();
                        setExistingAccountNotice(false);
                        setEmailLocked(false);
                        window.location.href = resolvePostLoginHref('/dashboard');
                        return;
                    }
                } catch (unlockCheckErr) {
                    console.warn('Unlock-Check vor Bootstrap fehlgeschlagen — fahre mit Onboarding fort:', unlockCheckErr);
                }
                try {
                    await bootstrapP1Documents(cred.user, {
                        ...pendingPayload,
                        email: pendingPayload.email || cred.user.email || email,
                    }, { skipEmailVerification: true });
                    clearPendingExistingAuth();
                    setExistingAccountNotice(false);
                    setEmailLocked(false);
                    showToast('Konto verknüpft! Weiter zum Onboarding...', 'success');
                    // Hart /onboarding — kein resolvePostLoginHref (sonst Dashboard-Bypass)
                    window.location.href = '/onboarding';
                    return;
                } catch (bootErr: any) {
                    console.error('P1-Bootstrap nach Bestandslogin fehlgeschlagen:', bootErr);
                    clearPendingExistingAuth();
                    showToast(
                        String(bootErr?.message || '').includes('Doppelvergabe')
                            ? 'Stopp, Doppelvergabe.'
                            : 'Login ok, aber P1-Akte konnte nicht angelegt werden. Weiter zum Onboarding.',
                        'error'
                    );
                    window.location.href = '/onboarding';
                    return;
                }
            }

            // Optional Prefill: DisplayName aus pebase_users
            try {
                const peBase = await readPebaseProfile(cred.user.uid);
                if ((peBase.firstName || peBase.lastName) && !cred.user.displayName) {
                    await updateProfile(cred.user, {
                        displayName: `${peBase.firstName} ${peBase.lastName}`.trim(),
                    });
                }
            } catch {
                /* ignore */
            }

            // Doppel-Schranke (Schleuse 2): nur kalkulation_mandanten/{uid} entscheidet Dashboard vs Onboarding
            const gate = await routeAfterP1AkteCheck(cred.user.uid);
            if (gate === 'onboarding') {
                window.location.href = '/onboarding';
                return;
            }
            window.location.href = resolvePostLoginHref('/dashboard');
        } catch (err: unknown) {
            console.error('Login-Routing fehlgeschlagen — Zwang Onboarding:', err);
            window.location.href = '/onboarding';
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();

        // 🍯 Honeypot: befülltes unsichtbares Feld = Bot → Fake-Success, kein einziger Schreibzugriff
        if (botField.trim() !== '') {
            showToast("Account erstellt! Weiter zum Onboarding...", "success");
            return;
        }

        if (isExistingClient === null) return showToast("Bitte gib an, ob du bereits Mandant bist.", "error");
        if (isSolo === null) return showToast("Bitte beantworte die erste Frage zu deinem Setup.", "error");
        if (!contractAccepted) return showToast("Bitte lies und bestätige die Vertragsvorschau.", "error");

        if (!firstName || !lastName || !email || password.length < 6) {
            return showToast("Bitte fülle alle Pflichtfelder aus (Passwort min. 6 Zeichen).", "error");
        }

        // 🛡️ Client-Rate-Limit: max. 1 echter Registrierungsversuch pro 30s pro Tab
        // (zählt erst NACH bestandener Feld-Validierung — Tippfehler-Korrekturen bleiben frei)
        try {
            const lastTs = Number(sessionStorage.getItem(P1_REGISTER_ATTEMPT_TS_KEY) || 0);
            if (Date.now() - lastTs < P1_REGISTER_COOLDOWN_MS) {
                return showToast("Zu viele Versuche. Bitte warte einen Moment und versuche es dann erneut.", "error");
            }
            sessionStorage.setItem(P1_REGISTER_ATTEMPT_TS_KEY, String(Date.now()));
        } catch { /* ignore */ }

        setIsLoading(true);
        let createdUser: import('firebase/auth').User | null = null;
        try {
            // Zugangsmonat setzt das System automatisch (= Registrierungsmonat) — kein User-Input
            const nowZugang = new Date();
            const zugangJahr = String(nowZugang.getFullYear());
            const zugangMonat = String(nowZugang.getMonth() + 1).padStart(2, '0');
            const zugangCore =
                isValidMonthYearCore(`${zugangMonat}/${zugangJahr}`)
                    ? `${zugangMonat}/${zugangJahr}`
                    : `${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`;
            // Premium-/Jahr-Logik bleibt am Registrierungsjahr (mind. 2027)
            const yearNum = Math.max(2027, new Date().getFullYear());
            const aktuellesJahr = String(yearNum);
            let finalMandantNr = '';
            let providedOldId = '';
            // Scheibe 1: Kundennummern-Layer (additiv, unabhängig von mandantNr)
            let existingCustomerNumber = '';
            let customerNumber = 'PENDING';
            const resolvedTarifStatus = isSolo ? 'Solo' : 'TeamPro';

            // Bestandskunde: ausschließlich Kundennummer wie 22/2022 (kein PEO/MA-I)
            if (isExistingClient === true) {
                const raw = String(providedMandantNr || '').trim().replace(/\s+/g, ' ');
                if (!raw) {
                    showToast("Bitte gib deine Mandant-Nummer ein (z.B. 22/2022).", "error");
                    setIsLoading(false);
                    return;
                }
                const core = normalizeCustomerNumberCore(raw);
                if (!core) {
                    showToast("Mandant-Nummer ungültig. Bitte im Format 22/2022 eingeben.", "error");
                    setIsLoading(false);
                    return;
                }
                providedOldId = core;
                existingCustomerNumber = core;
                customerNumber = core;
                // System-ID neu: Zugangsmonat heute + Tarif; Kd.-Nr. bleibt 22/2022 separat
                finalMandantNr = buildMandantNr({
                    core: `${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`,
                    coachInitial: null,
                    tarifStatus: resolvedTarifStatus,
                });
            }

            if (isExistingClient !== true) {
                // Kern = echter Zugangsmonat (MM/YYYY), nie laufende Nummer
                finalMandantNr = buildMandantNr({
                    core: zugangCore,
                    coachInitial: null,
                    tarifStatus: resolvedTarifStatus,
                });
            }

            const resolvedVertragsbeginn =
                isExistingClient === true
                    ? // Bestandsmandant: Tool-Start = Einstiegsmonat (heute), NICHT der Kern der alten Nr
                      `${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`
                    : zugangCore;

            const pendingPayload: PendingExistingAuthPayload = {
                email,
                firstName,
                lastName,
                phone,
                isExistingClient: !!isExistingClient,
                providedOldId,
                finalMandantNr,
                isSolo: !!isSolo,
                hasEmployeeEval: isSolo ? false : true,
                isTeamProPlus: false,
                tarifStatus: resolvedTarifStatus,
                packageTier: resolvedTarifStatus,
                registrationYear: aktuellesJahr,
                vertragsbeginn: resolvedVertragsbeginn,
                contractAccepted,
                isExistingCustomer: isExistingClient === true,
                existingCustomerNumber,
                customerNumber,
                billingStatus: 'pending_approval',
                sepaCancelPending: false,
                needsManualBestandReview: isExistingClient === true,
                bestandMatched: false,
            };

            // Zuerst Auth anlegen — sonst blockieren Firestore-Rules jeden Check/Counter
            let user: import('firebase/auth').User;
            try {
                const cred = await createUserWithEmailAndPassword(auth, email, password);
                user = cred.user;
                createdUser = user;
            } catch (authErr: unknown) {
                const code = authErr instanceof FirebaseError ? authErr.code : '';
                if (code === 'auth/email-already-in-use') {
                    // PEBase/P1 teilen denselben Auth-Pool: Login unter bestehender UID, dann Onboarding
                    savePendingExistingAuth(pendingPayload);
                    createdUser = null;
                    setIsLoading(false);
                    switchToExistingAccountLogin(email);
                    return;
                }
                throw authErr;
            }

            await bootstrapP1Documents(user, pendingPayload);

            // 💉 HIER WAR DER FEHLER: Sofortiger Redirect ins Onboarding, ohne Wartezeit.
            showToast("Account erstellt! Weiter zum Onboarding...", "success");
            window.location.href = '/onboarding';

        } catch (err: unknown) {
            if (createdUser) {
                try { await deleteUser(createdUser); } catch { /* orphan cleanup best-effort */ }
            }
            const error = err as Error;
            const code = err instanceof FirebaseError ? err.code : '';
            const msg =
                code === 'permission-denied' || /insufficient permissions/i.test(error.message || '')
                    ? 'Keine Berechtigung für diesen Schritt. Bitte Seite neu laden und erneut versuchen. Falls es bleibt: Support melden.'
                    : error.message;
            showToast("Fehler bei der Registrierung: " + msg, "error");
            setIsLoading(false);
        }
    };

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return showToast("Bitte E-Mail eingeben", "error");
        
        setIsLoading(true);
        try {
            auth.languageCode = 'de';
            await sendPasswordResetEmail(auth, email);
            showToast("Reset-Link gesendet! Prüfe dein Postfach.", "success");
            setView('login');
        } catch (err) {
            showToast("Fehler: E-Mail nicht gefunden.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const renderLanding = () => (
        <div className="text-center w-full animate-fadeIn">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-slate-400 text-xs font-mono uppercase tracking-widest mb-6 shadow-sm">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span> System Online
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight leading-[1.2]">
                Das Betriebssystem für ein<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E6C299] to-[#bf9b74]">erfolgreiches Beauty-Business</span>
            </h2>
            <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-16">
                Bitte logge dich ein oder erstelle einen neuen Account, um auf deine geschützte Umgebung zuzugreifen.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 w-full max-w-3xl mx-auto px-4 text-left">
                <div onClick={() => setView('login')} className="bg-[#0f172a]/50 backdrop-blur-sm border border-slate-800 hover:border-[#E6C299]/50 p-10 rounded-3xl cursor-pointer group transition-all duration-300 hover:shadow-[0_0_50px_rgba(230,194,153,0.05)] flex flex-col items-center text-center hover:-translate-y-1">
                    <div className="w-20 h-20 bg-slate-800/80 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-slate-700 group-hover:border-[#E6C299]/50 shadow-xl">
                        <LogIn className="w-10 h-10 text-[#E6C299] transition-colors" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">System Login</h3>
                    <p className="text-sm text-slate-500 mb-8 leading-relaxed">Für Mandanten mit bestehendem PreisExpert-Zugang.</p>
                    <button className="px-8 py-4 rounded-xl border border-slate-600 text-slate-300 text-sm font-bold group-hover:bg-[#E6C299] group-hover:text-black group-hover:border-[#E6C299] transition-all uppercase tracking-widest mt-auto w-full">Anmelden</button>
                </div>
                
                <div onClick={() => setView('register')} className="bg-[#0f172a] border border-slate-800 hover:border-slate-600 p-10 rounded-3xl cursor-pointer group transition-all duration-300 hover:shadow-2xl flex flex-col items-center text-center relative overflow-hidden hover:-translate-y-1">
                    <div className="w-20 h-20 bg-slate-900 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-slate-800 shadow-inner">
                        <UserPlus className="w-10 h-10 text-slate-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">Account erstellen</h3>
                    <p className="text-sm text-slate-500 mb-8 leading-relaxed">Neu hier oder Alt-Mandant? Erstelle hier deinen Systemzugang.</p>
                    <button className="px-8 py-4 rounded-xl bg-slate-800 text-slate-300 text-sm font-bold hover:bg-slate-700 hover:text-white transition-all uppercase tracking-widest w-full mt-auto">Registrieren</button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-[#020617] text-slate-200 min-h-screen flex flex-col font-sans relative overflow-x-hidden">
            
            {/* 📡 CHIRURGEN-MONITOR */}
            {rawFirebasePayload && rawSoloPrice === 0 && (
                <div className="fixed top-24 right-4 md:right-8 bg-red-950/95 text-white p-5 rounded-2xl z-[9999] border-2 border-red-500/50 shadow-[0_0_50px_rgba(239,68,68,0.3)] backdrop-blur-md w-[320px] animate-fadeIn">
                    <div className="flex items-center gap-2 mb-3 border-b border-red-500/30 pb-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span>
                        <p className="font-bold text-red-400 text-sm uppercase tracking-wider">System Radar Info</p>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed mb-3">
                        Datenbank ist verbunden, aber der gesuchte Schlüssel (z.B. <code className="bg-black/50 px-1 py-0.5 rounded text-white font-mono">Insider_Solo</code>) liefert <span className="font-bold text-white">0</span>. 
                        Hier sind die Daten, die Firebase gesendet hat:
                    </p>
                    <div className="bg-black/60 p-3 rounded-lg border border-slate-800 max-h-40 overflow-y-auto custom-scrollbar font-mono text-[10px] text-emerald-400 space-y-1">
                        {Object.keys(rawFirebasePayload).length > 0 ? (
                            Object.entries(rawFirebasePayload).map(([k, v]) => (
                                <div key={k} className="flex justify-between border-b border-slate-800/50 pb-1">
                                    <span className="text-slate-300 truncate mr-2">"{k}":</span>
                                    <span>{typeof v === 'object' ? '{Objekt}' : String(v)}</span>
                                </div>
                            ))
                        ) : (
                            <div className="text-slate-500">Das Dokument ist komplett leer.</div>
                        )}
                    </div>
                </div>
            )}

            <header className="w-full border-b border-slate-800 bg-[#0a0f1c] px-6 py-4 flex items-center justify-between sticky top-0 z-50">
                <div className="flex items-center gap-4">
                    <img src={PE_ONE_LOGO_SRC} alt="PE One Logo" className="h-16 md:h-20 object-contain shadow-[0_0_15px_rgba(230,194,153,0.1)] rounded-lg" />
                    <div className="hidden sm:block">
                        <h1 className="text-xl md:text-2xl font-bold text-white tracking-tight"><span className="text-[#E6C299]">Preis</span>Expert</h1>
                        <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-widest font-bold">Das Betriebssystem für ein erfolgreiches Beauty-Business</p>
                    </div>
                </div>
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-slate-400 text-xs font-mono uppercase tracking-widest shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span> System Online
                </div>
            </header>

            <main className="flex-1 flex flex-col items-center justify-center w-full p-4 md:p-8">
                <div className="w-full max-w-4xl">
                    
                    {view === 'landing' && renderLanding()}

                    {view === 'register' && (
                        <div className="bg-[#0f172a] border border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-fadeIn w-full">
                            <div className="p-6 md:p-10">
                                <button onClick={() => setView('landing')} className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors mb-8 text-sm font-bold uppercase tracking-wider">
                                    <ArrowLeft className="w-4 h-4" /> Zurück
                                </button>
                                
                                <div className="mb-10 pb-8 border-b border-slate-800/50 text-center md:text-left">
                                    <h3 className="text-white font-bold text-3xl md:text-4xl tracking-tight mb-4">Hallo und herzlich willkommen!</h3>
                                    <p className="text-[#E6C299] text-lg mb-2 font-medium">Wir freuen uns sehr, dass du uns dein Vertrauen schenkst.</p>
                                    <p className="text-slate-400 text-base leading-relaxed max-w-3xl">
                                        Um direkt durchstarten zu können, werden wir in den nächsten Schritten deine Basisdaten erfassen und deinen gesicherten System-Account erstellen. 
                                        Im Anschluss an diese Registrierung begrüßen wir dich in unserem geschützten Onboarding-Bereich, wo wir alle weiteren Details gemeinsam aufbauen.
                                    </p>
                                </div>

                                <div className="mb-8 max-w-xl rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden shadow-xl">
                                    <div className="relative w-full pt-[56.25%] bg-black border-b border-slate-800">
                                        <iframe
                                            className="absolute inset-0 w-full h-full border-0"
                                            src={REGISTER_GUIDE_VIMEO_EMBED}
                                            title="PEOne Registrierung – Account-Setup & Onboarding"
                                            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                                            allowFullScreen
                                            loading="lazy"
                                            referrerPolicy="strict-origin-when-cross-origin"
                                        />
                                    </div>
                                    <div className="p-5 flex items-start gap-3">
                                        <PlayCircle className="w-5 h-5 text-[#E6C299] shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-black text-white uppercase tracking-wider">Kurz ansehen vor dem Start</p>
                                            <p className="text-sm text-slate-300 mt-1 leading-relaxed">
                                                Ca. 2–3 Minuten: Registrierung, Account-Setup und Onboarding — dann weißt du genau, was als Nächstes kommt.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <form onSubmit={handleRegister} className="space-y-10">
                                    {/* 🍯 Honeypot — unsichtbar für Menschen, Bots füllen es aus */}
                                    <input
                                        type="text"
                                        name="b_phone_check"
                                        value={botField}
                                        onChange={e => setBotField(e.target.value)}
                                        style={{ display: 'none' }}
                                        tabIndex={-1}
                                        autoComplete="off"
                                        aria-hidden="true"
                                    />

                                    <div className="bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-slate-800">
                                        <label className="block text-sm font-bold text-white uppercase tracking-wider mb-4">1. Dein Status bei uns</label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <button 
                                                type="button"
                                                onClick={() => { setIsExistingClient(false); setProvidedMandantNr(''); }}
                                                className={`p-6 rounded-xl border-2 flex items-center gap-4 transition-all text-left ${isExistingClient === false ? 'bg-[#E6C299]/10 border-[#E6C299] text-white shadow-lg' : 'bg-[#0a0f1c] border-slate-800 text-slate-400 hover:border-slate-700'}`}
                                            >
                                                <UserCircle className={`w-8 h-8 ${isExistingClient === false ? 'text-[#E6C299]' : 'text-slate-500'}`} />
                                                <div>
                                                    <div className="font-bold text-base uppercase tracking-wider">Ich bin Neu</div>
                                                    <div className="text-xs mt-1 opacity-80">Ich möchte ein neues Setup buchen.</div>
                                                </div>
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={() => setIsExistingClient(true)}
                                                className={`p-6 rounded-xl border-2 flex items-center gap-4 transition-all text-left ${isExistingClient === true ? 'bg-blue-900/20 border-blue-500 text-white shadow-lg' : 'bg-[#0a0f1c] border-slate-800 text-slate-400 hover:border-slate-700'}`}
                                            >
                                                <ShieldCheck className={`w-8 h-8 ${isExistingClient === true ? 'text-blue-400' : 'text-slate-500'}`} />
                                                <div>
                                                    <div className="font-bold text-base uppercase tracking-wider">Bestehender Mandant</div>
                                                    <div className="text-xs mt-1 opacity-80">Ich habe bereits eine Mandant-Nummer.</div>
                                                </div>
                                            </button>
                                        </div>

                                        {isExistingClient === true && (
                                            <div className="mt-6 animate-fadeIn space-y-6">
                                                <div className="bg-blue-900/40 border-2 border-blue-500/60 p-6 rounded-xl flex gap-4 items-start shadow-lg">
                                                    <AlertCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                                                    <div className="space-y-1">
                                                        <span className="text-white font-bold text-sm uppercase tracking-wider block">Wichtige Migrations-Anweisung</span>
                                                        <p className="text-blue-200 text-sm md:text-base leading-relaxed font-medium">
                                                            Bist du bereits PreisExpert-Mandant?{' '}
                                                            <span className="text-white underline decoration-[#E6C299] underline-offset-2 font-bold">WICHTIG:</span>{' '}
                                                            Nutze für die Registrierung <span className="text-white font-bold underline">UNBEDINGT</span> exakt die E-Mail-Adresse aus deinem aktuellen Excel-Kalkulationsprogramm
                                                            {' '}— und trage darunter deine <span className="text-white font-bold underline">bestehende Mandant-Nummer</span> ein
                                                            {' '}(z.&nbsp;B. <span className="text-white font-mono font-bold">22/2022</span>), exakt wie bisher.
                                                            E-Mail ist der primäre Prüfwert; die Mandant-Nummer der zweite. Stimmen Adresse oder Nummer nicht, erscheint ein Hinweis zur manuellen Prüfung.
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="bg-blue-900/10 border border-blue-900/30 p-6 rounded-xl">
                                                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-wider">Bestehende Mandant-Nummer *</label>
                                                    <input
                                                        type="text"
                                                        value={providedMandantNr}
                                                        onChange={(e) =>
                                                            setProvidedMandantNr(
                                                                e.target.value.replace(/[^\d./-]/g, '')
                                                            )
                                                        }
                                                        placeholder="z.B. 22/2022"
                                                        className="w-full bg-[#08090b] border border-blue-500/30 text-white rounded-lg p-3 text-sm focus:border-blue-500 outline-none font-mono"
                                                        required
                                                    />
                                                    <p className="text-xs text-slate-400 mt-4 border-t border-blue-900/30 pt-4 leading-relaxed">
                                                        <ShieldCheck className="w-4 h-4 inline mr-1 text-blue-400" /> 
                                                        Deine Mandant-Nummer (z.&nbsp;B. <span className="font-mono text-slate-200">22/2022</span>) und die Excel-E-Mail ordnen dich als Bestandskunde zu.
                                                        <span className="text-slate-200 font-semibold"> Start in diesem Tool</span> ist der Monat deiner Registrierung.
                                                        Dein bestehender Vertrag behält seine Gültigkeit. Alte Excel-Dateien werden nicht übernommen — nur Berechnungswerte nach erfolgreicher Zuordnung.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {isExistingClient !== null && (
                                        <div className="bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-slate-800 animate-fadeIn">
                                            
                                            {/* SAUBERE INFO-BOX FÜR NEUKUNDEN (Über dem Business-Setup) */}
                                            {isExistingClient === false && neukundenAufschlag > 0 && (
                                                <div className="mb-8 bg-[#0a0f1c] border border-[#E6C299]/30 p-6 md:p-8 rounded-2xl shadow-[0_0_25px_rgba(230,194,153,0.05)] animate-fadeIn">
                                                    <div className="flex items-start gap-4 md:gap-5">
                                                        <div className="w-10 h-10 bg-[#E6C299]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1 border border-[#E6C299]/30">
                                                            <Info className="w-5 h-5 text-[#E6C299]" />
                                                        </div>
                                                        <div>
                                                            <h4 className="text-white font-bold text-lg md:text-xl mb-3">Warum ein Neukunden-Aufschlag?</h4>
                                                            <p className="text-slate-300 leading-relaxed text-sm md:text-base">
                                                                Neukunden verursachen bei der Einrichtung und im System-Onboarding deutlich mehr Aufwand. Daher berechnen wir im ersten Jahr einen initialen Aufschlag von <strong className="text-white bg-slate-800 px-2 py-0.5 rounded">{neukundenAufschlag}%</strong> auf unsere Tarife.
                                                            </p>
                                                            <div className="mt-4 p-4 bg-[#E6C299]/5 rounded-xl border border-[#E6C299]/20">
                                                                <p className="text-[#E6C299] font-bold text-sm">
                                                                    Wir belohnen treue Bestandskunden:
                                                                </p>
                                                                <p className="text-slate-400 text-sm mt-1">
                                                                    Ab dem 2. Jahr fällst du automatisch in den günstigeren Bestandskunden-Tarif und dein monatlicher Beitrag wird um diese {neukundenAufschlag}% gesenkt!
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <label className="block text-sm font-bold text-white uppercase tracking-wider mb-4">2. Dein Business-Setup</label>
                                            
                                            <div className="space-y-6">
                                                <div className="bg-[#0a0f1c] border border-slate-800 p-6 rounded-xl">
                                                    <p className="text-white font-bold mb-1">Arbeitest du aktuell mit produktiven Mitarbeitern?</p>
                                                    <p className="text-xs text-slate-400 mb-4">(Rezeptionskräfte, Reinigungshilfen, etc. ohne eigenen Umsatz zählen nicht dazu.)</p>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        
                                                        {/* Team Button (Links) */}
                                                        <button 
                                                            type="button" 
                                                            onClick={() => { setIsSolo(false); setHasEmployeeEval(true); setContractAccepted(false); }} 
                                                            className={`p-4 rounded-xl border-2 transition-all flex flex-col items-start text-left w-full h-full ${isSolo === false ? 'bg-[#E6C299]/10 border-[#E6C299] text-white shadow-md' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`}
                                                        >
                                                            <span className="font-bold text-base">mit Team</span>
                                                            <span className="text-xs text-slate-400 mt-1 mb-3 leading-relaxed">
                                                                Inklusive Mitarbeiter-Auswertung, Leistungslohnsystem &amp; allen Team-Tools.
                                                            </span>
                                                            <div className="mt-auto w-full border-t border-slate-700/50 pt-3">
                                                                <p className="text-xs text-slate-400 mb-1">PEOne Team Tarif (bis {renderPrice(teamplusThreshold)} € Umsatz):</p>
                                                                <div className="font-mono text-[#E6C299] text-lg font-bold">
                                                                    {renderPrice(teamPrice)} € <span className="text-xs text-slate-500 font-sans font-normal">/ Monat (Netto)</span>
                                                                    {isExistingClient === false && neukundenAufschlag > 0 && (
                                                                        <span className="text-[10px] text-slate-500 font-sans ml-2">(regulär {renderPrice(rawTeamPrice)} €)</span>
                                                                    )}
                                                                </div>
                                                                <div className="mt-2 pt-2 border-t border-slate-800/80">
                                                                    <p className="text-[11px] text-slate-400">Team-Plus automatisch ab {renderPrice(teamplusThreshold)} € Netto-Umsatz: <span className="font-mono text-[#E6C299] font-bold">ab {renderPercent(teamplusPercentage)}%</span></p>
                                                                </div>
                                                                {isExistingClient === false && neukundenAufschlag > 0 && <div className="text-[10px] text-slate-500 mt-1">Inkl. {neukundenAufschlag}% Neukunden-Aufschlag</div>}
                                                                {isExistingClient === true && <div className="text-[10px] text-emerald-500/70 mt-1">Bestandskunden-Tarif angewendet</div>}
                                                            </div>
                                                        </button>

                                                        {/* Solo Button (Rechts) */}
                                                        <button 
                                                            type="button" 
                                                            onClick={() => { setIsSolo(true); setHasEmployeeEval(false); setContractAccepted(false); }} 
                                                            className={`p-4 rounded-xl border-2 transition-all flex flex-col items-start text-left w-full h-full ${isSolo === true ? 'bg-[#E6C299]/10 border-[#E6C299] text-white shadow-md' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`}
                                                        >
                                                            <span className="font-bold text-base">ohne Team</span>
                                                            <span className="text-xs text-slate-400 mt-1 mb-3 leading-relaxed">
                                                                Für Einzelunternehmer ohne produktive Mitarbeiter.
                                                            </span>
                                                            <div className="mt-auto w-full border-t border-slate-700/50 pt-3">
                                                                <p className="text-xs text-slate-400 mb-1">PEOne Solo Tarif:</p>
                                                                <div className="font-mono text-[#E6C299] text-lg font-bold">
                                                                    {renderPrice(soloPrice)} € <span className="text-xs text-slate-500 font-sans font-normal">/ Monat (Netto)</span>
                                                                    {isExistingClient === false && neukundenAufschlag > 0 && (
                                                                        <span className="text-[10px] text-slate-500 font-sans ml-2">(regulär {renderPrice(rawSoloPrice)} €)</span>
                                                                    )}
                                                                </div>
                                                                
                                                                {isExistingClient === false && neukundenAufschlag > 0 && <div className="text-[10px] text-slate-500 mt-1">Inkl. {neukundenAufschlag}% Neukunden-Aufschlag</div>}
                                                                {isExistingClient === true && <div className="text-[10px] text-emerald-500/70 mt-1">Bestandskunden-Tarif angewendet</div>}
                                                            </div>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {isSolo !== null && (
                                                <div className="mt-8 pt-6 border-t border-slate-800 animate-fadeIn">
                                                    <button 
                                                        type="button" 
                                                        onClick={() => setIsContractModalOpen(true)}
                                                        className="flex items-center justify-center gap-3 bg-slate-800 hover:bg-slate-700 text-white px-6 py-4 rounded-xl border border-slate-600 transition-colors w-full md:w-auto shadow-md"
                                                    >
                                                        <FileText className="w-5 h-5 text-[#E6C299]" /> 
                                                        <span className="font-bold tracking-wide">📄 Vorab-Vertragsvorschau lesen</span>
                                                    </button>
                                                    
                                                    <label className="flex items-start mt-6 cursor-pointer group bg-slate-900/80 p-4 rounded-xl border border-slate-700 hover:border-slate-500 transition-colors">
                                                        <div className="flex items-center h-6">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={contractAccepted} 
                                                                onChange={e => setContractAccepted(e.target.checked)} 
                                                                className="w-5 h-5 rounded border-slate-600 text-[#E6C299] focus:ring-[#E6C299] bg-black"
                                                            />
                                                        </div>
                                                        <div className="ml-4">
                                                            <span className={`text-sm md:text-base font-bold transition-colors ${contractAccepted ? 'text-[#E6C299]' : 'text-slate-300 group-hover:text-white'}`}>
                                                                Ich akzeptiere die AGB und habe die{' '}
                                                                <a
                                                                    href="https://www.friseur-coaching.de/datenschutzerklaerung/"
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="underline text-[#E6C299] hover:text-white"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    Datenschutzerklärung
                                                                </a>
                                                                {' '}zur Kenntnis genommen.
                                                            </span>
                                                            <p className="text-xs text-slate-500 mt-1">Die rechtsgültige, finale Vertragsbestätigung erfolgt erst nach Eingabe deiner persönlichen Salon-Details im Onboarding.</p>
                                                        </div>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {isExistingClient !== null && (
                                        <div className="bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-slate-800 animate-fadeIn">
                                            <label className="block text-sm font-bold text-white uppercase tracking-wider mb-6">3. Persönliche Daten & System-Settings</label>
                                            
                                            <div className="space-y-6">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-wider">Vorname</label>
                                                        <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required className="w-full bg-[#0a0f1c] border border-slate-700 rounded-xl p-4 text-white focus:border-[#E6C299] outline-none" placeholder="Max" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-wider">Nachname</label>
                                                        <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required className="w-full bg-[#0a0f1c] border border-slate-700 rounded-xl p-4 text-white focus:border-[#E6C299] outline-none" placeholder="Mustermann" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-wider">E-Mail Adresse (System-Login)</label>
                                                    <div className="relative">
                                                        <Mail className="absolute left-4 top-4 w-5 h-5 text-slate-500" />
                                                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full bg-[#0a0f1c] border border-slate-700 rounded-xl p-4 pl-12 text-white focus:border-[#E6C299] outline-none" placeholder="mail@example.de" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2 tracking-wider">Passwort (min. 6 Zeichen)</label>
                                                    <div className="relative">
                                                        <Key className="absolute left-4 top-4 w-5 h-5 text-slate-500" />
                                                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="w-full bg-[#0a0f1c] border border-slate-700 rounded-xl p-4 pl-12 pr-12 text-white focus:border-[#E6C299] outline-none" placeholder="••••••••" />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowPassword(v => !v)}
                                                            tabIndex={-1}
                                                            aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                                            title={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                                            className="absolute right-4 top-4 text-slate-500 hover:text-[#E6C299] transition-colors"
                                                        >
                                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <button 
                                                disabled={isLoading || (!contractAccepted)} 
                                                type="submit" 
                                                className={`w-full py-5 font-bold rounded-xl shadow-lg transition-all text-base md:text-lg uppercase tracking-widest flex justify-center items-center gap-3 mt-10 ${isLoading || (!contractAccepted) ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-[#E6C299] text-black hover:bg-[#d4b08c]'}`}
                                            >
                                                {isLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Check className="w-6 h-6" /> Account verbindlich erstellen</>}
                                            </button>
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    )}

                    {view === 'login' && (
                        <div className="bg-[#0f172a] border border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-fadeIn w-full max-w-xl mx-auto">
                            <div className="p-8 md:p-12">
                                <button onClick={() => { setExistingAccountNotice(false); setEmailLocked(false); setView('landing'); }} className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors mb-8 text-sm font-bold uppercase tracking-wider">
                                    <ArrowLeft className="w-4 h-4" /> Zurück
                                </button>
                                <div className="text-center mb-10">
                                    <div className="w-20 h-20 bg-[#E6C299]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#E6C299]/30">
                                        <Key className="w-10 h-10 text-[#E6C299]" />
                                    </div>
                                    <h3 className="text-white font-bold text-3xl tracking-tight">System Login</h3>
                                </div>
                                {existingAccountNotice && (
                                    <div className="mb-6 rounded-xl border border-[#E6C299]/40 bg-[#E6C299]/10 px-4 py-3 text-sm text-[#E6C299] leading-relaxed flex gap-3 items-start">
                                        <Info className="w-5 h-5 shrink-0 mt-0.5" />
                                        <span>
                                            Ein PreisExpert-Konto existiert bereits für diese E-Mail-Adresse (z.&nbsp;B. aus PEBase).
                                            Bitte melde dich an, um mit dem Onboarding fortzufahren.
                                        </span>
                                    </div>
                                )}
                                <form onSubmit={handleLogin} className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-200 mb-2">E-Mail</label>
                                        <div className="relative">
                                            <Mail className="absolute left-4 top-4 w-5 h-5 text-slate-500" />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={e => { if (!emailLocked) setEmail(e.target.value); }}
                                                readOnly={emailLocked}
                                                className={`w-full bg-[#0a0f1c] border border-slate-700 rounded-xl p-4 pl-12 text-white focus:border-[#E6C299] outline-none transition-all ${emailLocked ? 'opacity-80 cursor-not-allowed' : ''}`}
                                                placeholder="deine@email.de"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-200 mb-2">Passwort</label>
                                        <div className="relative">
                                            <Key className="absolute left-4 top-4 w-5 h-5 text-slate-500" />
                                            <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required className="w-full bg-[#0a0f1c] border border-slate-700 rounded-xl p-4 pl-12 pr-12 text-white focus:border-[#E6C299] outline-none transition-all" placeholder="••••••••" />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(v => !v)}
                                                tabIndex={-1}
                                                aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                                title={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                                className="absolute right-4 top-4 text-slate-500 hover:text-[#E6C299] transition-colors"
                                            >
                                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                    </div>
                                    <button disabled={isLoading} type="submit" className="w-full py-4 bg-[#E6C299] text-black font-bold rounded-xl shadow-lg hover:bg-[#d4b08c] transition-all text-lg uppercase tracking-widest mt-4 flex justify-center items-center gap-2">
                                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ArrowRight className="w-5 h-5" /> Anmelden</>}
                                    </button>
                                    <button type="button" onClick={() => setView('reset')} className="block w-full text-center text-sm text-slate-400 hover:text-white transition-colors underline pt-4">Passwort vergessen?</button>
                                </form>
                            </div>
                        </div>
                    )}

                    {view === 'reset' && (
                        <div className="bg-[#0f172a] border border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-fadeIn w-full max-w-xl mx-auto">
                            <div className="p-8 md:p-12">
                                <form onSubmit={handleReset} className="animate-fadeIn">
                                    <div className="text-center mb-8">
                                        <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-600">
                                            <MailQuestion className="w-8 h-8 text-slate-300" />
                                        </div>
                                        <h3 className="text-white font-bold text-2xl tracking-tight">Passwort Reset</h3>
                                    </div>
                                    <div className="mb-8">
                                        <label className="block text-sm font-bold text-slate-200 mb-2">E-Mail Adresse</label>
                                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full bg-[#0a0f1c] border border-slate-700 rounded-xl p-4 text-white focus:border-[#E6C299] outline-none" placeholder="deine@email.de" />
                                    </div>
                                    <button disabled={isLoading} type="submit" className="w-full py-4 bg-slate-700 text-white font-bold rounded-xl shadow-lg hover:bg-slate-600 transition-all text-lg mb-4 flex justify-center items-center">
                                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Link anfordern"}
                                    </button>
                                    <button type="button" onClick={() => setView('login')} className="block w-full text-center text-sm text-slate-400 hover:text-white transition-colors">Zurück zum Login</button>
                                </form>
                            </div>
                        </div>
                    )}

                </div>
            </main>

            <footer className="w-full border-t border-slate-800 bg-[#0a0f1c] p-6 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-sm text-slate-500">
                <span>&copy; {new Date().getFullYear()} PreisExpert. Alle Rechte vorbehalten.</span>
                <div className="flex items-center gap-6">
                    <a href="https://www.friseur-coaching.de/impressum" target="_blank" rel="noopener noreferrer" className="hover:text-[#E6C299] transition-colors">Impressum</a>
                    <a href="https://www.friseur-coaching.de/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer" className="hover:text-[#E6C299] transition-colors">Datenschutzerklärung</a>
                    <a href="https://www.friseur-coaching.de/agb" target="_blank" rel="noopener noreferrer" className="hover:text-[#E6C299] transition-colors">AGB</a>
                </div>
            </footer>

            {/* VERTRAGS-POPUP MODAL */}
            {isContractModalOpen && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 animate-fadeIn">
                    <div className="bg-[#0f172a] border border-slate-700 rounded-3xl shadow-[0_0_50px_rgba(0,0,0,0.8)] w-full max-w-4xl max-h-[90vh] flex flex-col relative">
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-[#0a0f1c] rounded-t-3xl">
                            <h3 className="text-white font-bold text-xl flex items-center gap-2">
                                <FileText className="w-5 h-5 text-[#E6C299]" /> Vertragsvorschau
                            </h3>
                            <button onClick={() => setIsContractModalOpen(false)} className="text-slate-500 hover:text-white transition-colors bg-slate-800 p-2 rounded-full">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <div className="p-6 md:p-10 overflow-y-auto custom-scrollbar">
                            {(!isSolo && !hasEmployeeEval) ? (
                                <ContractPreviewBasic
                                    settings={mergedSettings}
                                    coaches={coaches}
                                    isNewCustomer={isExistingClient === false}
                                />
                            ) : (
                                <ContractPreviewInsider
                                    settings={mergedSettings}
                                    coaches={coaches}
                                    isSolo={isSolo === true}
                                    isNewCustomer={isExistingClient === false}
                                />
                            )}
                        </div>

                        <div className="p-6 border-t border-slate-800 bg-[#0a0f1c] rounded-b-3xl text-right">
                            <button 
                                onClick={() => { setIsContractModalOpen(false); setContractAccepted(true); }} 
                                className="bg-[#E6C299] hover:bg-[#d4b08c] text-black font-bold px-8 py-3 rounded-xl transition-colors uppercase tracking-widest text-sm"
                            >
                                Gelesen & Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TOASTS */}
            <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[250] transform transition-all duration-300 ${toast.visible ? 'translate-y-0 opacity-100' : 'translate-y-[100px] opacity-0'} bg-slate-800 border border-slate-600 text-white px-6 py-4 rounded-2xl shadow-[0_0_30px_rgba(0,0,0,0.5)] flex items-center gap-3 w-max`}>
                <div className={`p-1.5 rounded-full ${toast.type === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                    {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                </div>
                <span className="font-bold text-sm">{toast.msg}</span>
            </div>
        </div>
    );
};

export default AuthGatewayPage;